import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BarChart3, Users as UsersIcon, Search as SearchIcon, DollarSign, TrendingUp, Loader2, Ban, Check } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);
  const [topDomains, setTopDomains] = useState([]);
  const [users, setUsers] = useState([]);
  const [scans, setScans] = useState([]);

  useEffect(() => {
    if (!user || user === false || user.role !== "admin") return;
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/timeseries?days=14").then((r) => setSeries(r.data)),
      api.get("/admin/top-domains").then((r) => setTopDomains(r.data)),
      api.get("/admin/users?limit=50").then((r) => setUsers(r.data)),
      api.get("/admin/scans?limit=20").then((r) => setScans(r.data)),
    ]).catch(() => {});
  }, [user]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#9B9A97]">Loading…</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-6" data-testid="admin-forbidden">
        <div className="text-center">
          <span className="text-4xl block mb-4">🔒</span>
          <h1 className="font-serif-h text-2xl font-semibold mb-2">Admin only</h1>
          <p className="text-[#787774] mb-4">You don&apos;t have permission to view this page.</p>
          <Link to="/" className="text-[#2383E2] hover:underline">← Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-[#37352F] min-h-[calc(100vh-3.5rem)]">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <span className="notion-emoji-box callout-purple">🛠️</span>
          <h1 className="font-serif-h text-3xl md:text-4xl font-semibold tracking-tight">Admin panel</h1>
        </div>
        <p className="text-[#787774] mb-10 ml-[52px]">Analytics, users, scans, and revenue at a glance.</p>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="admin-stats-grid">
          <StatCard emoji="👥" bg="callout-blue" label="Total users" value={stats?.total_users ?? "—"} sub={`${stats?.pro_users ?? 0} on Pro`} testId="stat-users" />
          <StatCard emoji="🔍" bg="callout-green" label="Total scans" value={stats?.total_scans ?? "—"} sub={`${stats?.guest_scans ?? 0} guest`} testId="stat-scans" />
          <StatCard emoji="📊" bg="callout-orange" label="Avg SEO / GEO" value={`${stats?.avg_seo ?? "—"} / ${stats?.avg_geo ?? "—"}`} testId="stat-avg" />
          <StatCard emoji="💰" bg="callout-yellow" label="Revenue (test)" value={`$${(stats?.total_revenue ?? 0).toFixed(2)}`} sub={`${stats?.pro_users ?? 0} paid users`} testId="stat-revenue" />
        </div>

        {/* Timeseries chart */}
        <section className="mb-10" data-testid="timeseries-chart">
          <div className="flex items-center gap-3 mb-4">
            <span className="notion-emoji-box callout-blue" style={{ width: 32, height: 32, fontSize: 18 }}>📈</span>
            <h2 className="font-serif-h text-2xl font-semibold">Last 14 days</h2>
          </div>
          <div className="border border-[#EBEBEA] rounded-lg p-4 bg-white h-[320px]">
            {series.length === 0 ? (
              <div className="h-full flex items-center justify-center text-[#9B9A97] text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading chart data…</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEA" />
                  <XAxis dataKey="date" stroke="#9B9A97" fontSize={11} />
                  <YAxis stroke="#9B9A97" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #EBEBEA", fontSize: 13 }} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="signups" stroke="#2383E2" strokeWidth={2} dot={{ r: 3 }} name="Signups" />
                  <Line type="monotone" dataKey="scans" stroke="#0F7B0F" strokeWidth={2} dot={{ r: 3 }} name="Scans" />
                  <Line type="monotone" dataKey="guest_scans" stroke="#D9730D" strokeWidth={2} dot={{ r: 3 }} name="Guest scans" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Top domains */}
        <section className="mb-10" data-testid="top-domains-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="notion-emoji-box callout-orange" style={{ width: 32, height: 32, fontSize: 18 }}>🏆</span>
            <h2 className="font-serif-h text-2xl font-semibold">Top scanned URLs</h2>
          </div>
          <div className="border border-[#EBEBEA] rounded-lg overflow-hidden">
            {topDomains.length === 0 ? (
              <div className="p-8 text-center text-[#9B9A97] text-sm">No data yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAF9] border-b border-[#EBEBEA]">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-[#787774]">URL</th>
                    <th className="text-right px-4 py-2 font-medium text-[#787774]">Scans</th>
                    <th className="text-right px-4 py-2 font-medium text-[#787774]">Avg SEO</th>
                    <th className="text-right px-4 py-2 font-medium text-[#787774]">Avg GEO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EBEBEA]">
                  {topDomains.map((d) => (
                    <tr key={d.url} className="hover:bg-[#FAFAF9]">
                      <td className="px-4 py-2 truncate max-w-md">{d.url}</td>
                      <td className="px-4 py-2 text-right font-semibold">{d.count}</td>
                      <td className="px-4 py-2 text-right">{d.avg_seo}</td>
                      <td className="px-4 py-2 text-right">{d.avg_geo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Recent signups */}
        <section className="mb-10" data-testid="recent-users-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="notion-emoji-box callout-green" style={{ width: 32, height: 32, fontSize: 18 }}>👤</span>
            <h2 className="font-serif-h text-2xl font-semibold">Users</h2>
            <span className="text-sm text-[#9B9A97]">({users.length})</span>
          </div>
          <div className="border border-[#EBEBEA] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF9] border-b border-[#EBEBEA]">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-[#787774]">Email</th>
                  <th className="text-left px-4 py-2 font-medium text-[#787774]">Plan</th>
                  <th className="text-left px-4 py-2 font-medium text-[#787774]">Role</th>
                  <th className="text-right px-4 py-2 font-medium text-[#787774]">Scans</th>
                  <th className="text-left px-4 py-2 font-medium text-[#787774]">Joined</th>
                  <th className="text-right px-4 py-2 font-medium text-[#787774]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EBEBEA]">
                {users.map((u) => (
                  <UserRow key={u.id} u={u} onChange={(patch) => setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, ...patch } : x)))} />
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Recent scans */}
        <section className="mb-10" data-testid="recent-scans-section">
          <div className="flex items-center gap-3 mb-4">
            <span className="notion-emoji-box callout-purple" style={{ width: 32, height: 32, fontSize: 18 }}>📝</span>
            <h2 className="font-serif-h text-2xl font-semibold">Recent scans</h2>
          </div>
          <div className="border border-[#EBEBEA] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF9] border-b border-[#EBEBEA]">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-[#787774]">URL</th>
                  <th className="text-left px-4 py-2 font-medium text-[#787774]">User</th>
                  <th className="text-right px-4 py-2 font-medium text-[#787774]">SEO</th>
                  <th className="text-right px-4 py-2 font-medium text-[#787774]">GEO</th>
                  <th className="text-left px-4 py-2 font-medium text-[#787774]">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EBEBEA]">
                {scans.map((s) => (
                  <tr key={s.id} className="hover:bg-[#FAFAF9]">
                    <td className="px-4 py-2 truncate max-w-sm">{s.url}</td>
                    <td className="px-4 py-2 text-[#787774]">{s.user_email || "—"}</td>
                    <td className="px-4 py-2 text-right font-semibold" style={{ color: scoreColor(s.seo_score) }}>{s.seo_score}</td>
                    <td className="px-4 py-2 text-right font-semibold" style={{ color: scoreColor(s.geo_score) }}>{s.geo_score}</td>
                    <td className="px-4 py-2 text-[#787774] text-xs">{new Date(s.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({ emoji, bg, label, value, sub, testId }) {
  return (
    <div className="border border-[#EBEBEA] rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`notion-emoji-box ${bg}`} style={{ width: 28, height: 28, fontSize: 16 }}>{emoji}</span>
        <span className="text-xs text-[#787774]">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-[#37352F]">{value}</div>
      {sub && <div className="text-xs text-[#9B9A97] mt-1">{sub}</div>}
    </div>
  );
}

function UserRow({ u, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.id}`, { disabled: !u.disabled });
      onChange({ disabled: !u.disabled });
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr className="hover:bg-[#FAFAF9]" data-testid={`admin-user-${u.id}`}>
      <td className="px-4 py-2 truncate">{u.email}</td>
      <td className="px-4 py-2">
        <span className={`text-xs px-2 py-0.5 rounded-md ${u.plan === "pro" ? "callout-blue text-[#2383E2]" : "callout-gray text-[#787774]"}`}>{u.plan}</span>
      </td>
      <td className="px-4 py-2 text-[#787774]">{u.role}</td>
      <td className="px-4 py-2 text-right">{u.scan_count}</td>
      <td className="px-4 py-2 text-[#787774] text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
      <td className="px-4 py-2 text-right">
        {u.role === "admin" ? (
          <span className="text-xs text-[#9B9A97]">—</span>
        ) : (
          <button
            onClick={toggle}
            disabled={busy}
            data-testid={`toggle-disable-${u.id}`}
            className={`text-xs px-2 py-1 rounded-md ${u.disabled ? "callout-green text-[#0F7B0F]" : "callout-red text-[#B0261D]"} hover:opacity-80 disabled:opacity-40 inline-flex items-center gap-1`}
          >
            {u.disabled ? <><Check className="w-3 h-3" /> Enable</> : <><Ban className="w-3 h-3" /> Disable</>}
          </button>
        )}
      </td>
    </tr>
  );
}

function scoreColor(v) {
  return v >= 80 ? "#0F7B0F" : v >= 60 ? "#D9730D" : "#E03E3E";
}
